/**
 * SSE Client using fetch with ReadableStream.
 *
 * Uses fetch instead of EventSource because EventSource doesn't support
 * custom headers (needed for Authorization).
 */

import { SSEConnection, SSEConnectionConfig, SSEConnectionState, SSE } from './types'

const DEFAULT_RECONNECT_CONFIG = {
  enabled: true,
  maxAttempts: 5,
  baseDelayMs: 1000,
  maxDelayMs: 30000,
}

/**
 * Parse a single SSE message block into an event object.
 *
 * SSE spec: https://html.spec.whatwg.org/multipage/server-sent-events.html
 */
function parseSSEMessage(messageBlock: string): SSE | null {
  const lines = messageBlock.split(/\r?\n/)
  const dataLines: string[] = []
  let eventType: string | undefined

  for (const line of lines) {
    // Comment lines start with `:` - ignore them
    if (line.startsWith(':')) {
      continue
    }

    if (!line.trim()) {
      continue
    }

    const colonIndex = line.indexOf(':')
    if (colonIndex === -1) {
      // `:foo` is a comment in the spec
      continue
    }

    const field = line.slice(0, colonIndex)
    let value = line.slice(colonIndex + 1)
    if (value.startsWith(' ')) {
      value = value.slice(1)
    }

    if (field === 'data') {
      dataLines.push(value)
    } else if (field === 'event') {
      eventType = value
    }
  }

  // No data lines means no event
  if (dataLines.length === 0) {
    return null
  }

  // Multiple data lines are joined with newlines per SSE spec
  const jsonStr = dataLines.join('\n')
  if (!jsonStr) {
    return null
  }

  try {
    const parsed = JSON.parse(jsonStr) as SSE
    if (eventType) {
      parsed.event = eventType
    }
    return parsed
  } catch (err) {
    console.warn('[SSE] Failed to parse event:', jsonStr, 'Error:', err)
    return null
  }
}

/**
 * Async generator that yields parsed SSE events from a ReadableStream.
 */
async function* streamSSEEvents(reader: ReadableStreamDefaultReader<Uint8Array>): AsyncGenerator<SSE> {
  const decoder = new TextDecoder()
  let buffer = ''

  for (let result = await reader.read(); !result.done; result = await reader.read()) {
    buffer += decoder.decode(result.value, { stream: true })

    // Split on blank lines
    const messages = buffer.split(/\r?\n\r?\n/)
    buffer = messages.pop() || ''

    for (const message of messages) {
      const event = parseSSEMessage(message)
      if (event) {
        yield event
      }
    }
  }

  // Flush any remaining buffer content when stream ends (handles abrupt disconnects without final delimiter)
  if (buffer.trim()) {
    const event = parseSSEMessage(buffer)
    if (event) {
      yield event
    }
  }
}

function calculateBackoffDelay(attempt: number, baseDelayMs: number, maxDelayMs: number): number {
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt)
  const jitter = Math.random() * 0.3 * exponentialDelay // 0-30% jitter
  return Math.min(exponentialDelay + jitter, maxDelayMs)
}

export function createSSEConnection<TEvent extends SSE = SSE>(config: SSEConnectionConfig<TEvent>): SSEConnection {
  const { url, getAuthToken, getOrganizationId, onEvent, onStateChange, onError, onDisconnected, reconnect } = config

  const reconnectConfig = {
    ...DEFAULT_RECONNECT_CONFIG,
    ...reconnect,
  }

  let state: SSEConnectionState = 'disconnected'
  let abortController: AbortController | null = null
  let reconnectAttempts = 0
  let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
  let isClosed = false
  let wasConnected = false

  const setState = (newState: SSEConnectionState) => {
    if (state !== newState) {
      const previousState = state
      state = newState
      onStateChange?.(newState)

      // Track if we've ever been connected
      if (newState === 'connected') {
        wasConnected = true
      }

      // Invoke onDisconnected when transitioning to disconnected after being connected
      if (newState === 'disconnected' && previousState !== 'disconnected' && wasConnected) {
        onDisconnected?.()
      }
    }
  }

  const cleanup = () => {
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId)
      reconnectTimeoutId = null
    }
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  const scheduleReconnect = () => {
    if (isClosed || !reconnectConfig.enabled) {
      setState('disconnected')
      return
    }

    if (reconnectAttempts >= reconnectConfig.maxAttempts) {
      console.warn(`[SSE] Max reconnection attempts (${reconnectConfig.maxAttempts}) reached`)
      setState('error')

      if (onError) {
        onError(new Error(`Failed to connect after ${reconnectConfig.maxAttempts} attempts`))
      }
      return
    }

    const delay = calculateBackoffDelay(reconnectAttempts, reconnectConfig.baseDelayMs, reconnectConfig.maxDelayMs)

    reconnectTimeoutId = setTimeout(() => {
      reconnectAttempts++
      void connect()
    }, delay)
  }

  const processStream = async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
    try {
      for await (const event of streamSSEEvents(reader)) {
        if (event.type === 'heartbeat') continue

        reconnectAttempts = 0

        if (event.type === 'connected') {
          setState('connected')
        }

        onEvent(event as TEvent)
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return
      }
      throw err
    }
  }

  const connect = async () => {
    if (isClosed) return

    cleanup()
    setState('connecting')
    abortController = new AbortController()

    // Get auth token - failures here should NOT trigger reconnection
    // (auth errors require user action, not retries)
    let token: string
    try {
      token = await getAuthToken()
    } catch (authErr) {
      console.error('[SSE] Authentication failed:', authErr)
      setState('error')
      onError?.(new Error(`Authentication failed: ${(authErr as Error).message}`))
      return // Do NOT schedule reconnection for auth failures
    }

    // Check if closed during async getAuthToken (e.g., component unmount)
    if (isClosed || !abortController) return

    try {
      const orgId = getOrganizationId?.()
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          ...(orgId && { 'x-organizationId': orgId }),
        },
        signal: abortController.signal,
      })

      if (!response.ok) {
        // Include response body for better error context
        let errorBody = ''
        try {
          errorBody = await response.text()
        } catch {
          // Ignore body read failure
        }
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`)
      }

      if (!response.body) {
        throw new Error('SSE response has no body')
      }

      const reader = response.body.getReader()

      await processStream(reader)

      // Stream ended normally; try to reconnect
      if (!isClosed) {
        scheduleReconnect()
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return
      }

      console.error(`[SSE] Connection error (attempt ${reconnectAttempts + 1}/${reconnectConfig.maxAttempts}):`, err)
      onError?.(err as Error)

      if (!isClosed) {
        scheduleReconnect()
      }
    }
  }

  void connect()

  return {
    close: () => {
      isClosed = true
      cleanup()
      setState('disconnected')
    },
    getState: () => state,
  }
}
